import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Reveal from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "DISRUPT FORM",
    text: "Style is not decoration — it is a decision made every morning about who you intend to be. We cover fashion, ink and street culture as craft: the tailor's shoulder, the tattoo artist's line, the writer's tag on a brick wall. Form is the first argument a man makes before he opens his mouth.",
  },
  {
    n: "02",
    title: "AMPLIFY VOICES",
    text: "A magazine that only talks is a lecture. MODERN MAN is a conversation: every member can publish an opinion, defend it in the comments, and put it to a vote. The best arguments rise. The loudest ones get tested. Your take belongs here — bring it.",
  },
  {
    n: "03",
    title: "SHAPE FUTURE",
    text: "Machines, neural interfaces, the next console generation, the car of 2035 — the future is not something that happens to men, it is something men build. We cover tomorrow early, honestly, and without the hype cycle. Read it here first, argue about it after.",
  },
];

export default function Manifesto() {
  const [open, setOpen] = useState(0);

  return (
    <section className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24 lg:py-32">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-kinetic mb-4">The Manifesto</p>
        <h2 className="font-display font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-16">
          Three things <span className="font-serif italic normal-case text-ash">we believe</span>
        </h2>
      </Reveal>

      <div className="border-t border-white/10">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08}>
            <div data-testid={`manifesto-chapter-item-${i}`} className="border-b border-white/10 group">
              <button
                data-testid={`manifesto-expand-button-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center gap-6 lg:gap-12 py-8 lg:py-10 text-left">
                <span className="font-mono text-kinetic text-sm shrink-0">{c.n}</span>
                <span className={`font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl lg:text-6xl transition-colors duration-300 flex-1 ${open === i ? "text-porcelain" : "text-outline group-hover:text-porcelain"}`}>
                  {c.title}
                </span>
                <motion.span animate={{ rotate: open === i ? 180 : 0 }} transition={{ duration: 0.4 }}
                  className="shrink-0 w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-ash group-hover:border-kinetic group-hover:text-kinetic transition-colors duration-300">
                  <ChevronDown size={16} />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden">
                    <p className="text-ash text-base lg:text-lg leading-relaxed max-w-3xl pb-10 pl-10 lg:pl-[4.5rem]">
                      {c.text}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
